import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// react-bootstrap
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';

// project-imports
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import useConfig from 'hooks/useConfig';
import { setResolvedTheme } from 'components/setResolvedTheme';
import { ThemeMode } from 'config';
import { useAuthStore } from 'store/authStore';
import { useMenuStore } from 'store/menuStore';
import { AuthService } from 'api/services/authService';
import ChangePasswordModal from 'components/ChangePasswordModal';
import MyAccountModal from 'components/MyAccountModal';
import ImpersonateModal from 'components/ImpersonateModal';
import GlobalSearch from 'components/GlobalSearch';
import NotificationsBell from 'components/NotificationsBell';


// types
import { I18n } from 'types/config';
import { fmtFechaHora } from 'utils/formatDate';


// =============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const { i18n, onChangeLocalization, onChangeMode, mode } = useConfig();
  const { user, clearAuth, impersonatedBy } = useAuthStore();
  const clearMenuItems = useMenuStore((s) => s.clearItems);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [lastLogout, setLastLogout] = useState<string | null>(null);

  useEffect(() => {
    if (user?.keyUser) {
      AuthService.getLastLogout(user.keyUser).then(setLastLogout).catch(() => {});
    }
  }, [user?.keyUser]);

  useEffect(() => {
    setResolvedTheme(mode);
  }, [mode]);

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const handleListItemClick = (lang: I18n) => {
    onChangeLocalization(lang);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
    } catch {
      // Si el API falla igual limpiamos la sesión local
    } finally {
      clearAuth();
      clearMenuItems();
    }
  };

  return (
    <>
    <header className="pc-header">
      <div className="header-wrapper">
        <div className="me-auto pc-mob-drp">
          <Nav className="list-unstyled">
            <Nav.Item className="pc-h-item pc-sidebar-collapse">
              <Nav.Link
                as={Link}
                to="#"
                className="pc-head-link ms-0"
                id="sidebar-hide"
                onClick={() => {
                  handlerDrawerOpen(!drawerOpen);
                }}
              >
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="pc-h-item pc-sidebar-popup">
              <Nav.Link as={Link} to="#" className="pc-head-link ms-0" id="mobile-collapse" onClick={() => handlerDrawerOpen(!drawerOpen)}>
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>

            <GlobalSearch />
          </Nav>
        </div>
        <div className="ms-auto">
          <Nav className="list-unstyled">
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle className="pc-head-link me-0 arrow-none" variant="link" id="dropdown-basic">
                <i className="ph ph-sun-dim" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="pc-h-dropdown">
                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.DARK)}>
                  <i className="ph ph-moon" />
                  Dark
                </Dropdown.Item>

                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.LIGHT)}>
                  <i className="ph ph-sun" />
                  Light
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onChangeMode(ThemeMode.AUTO)}>
                  <i className="ph ph-cpu" />
                  Default
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="pc-h-item d-none d-md-inline-flex" align="end">
              <Dropdown.Toggle className="pc-head-link head-link-primary me-0 arrow-none" variant="link" id="language-dropdown">
                <i className="ph ph-translate" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="pc-h-dropdown lng-dropdown">
                <Dropdown.Item active={i18n === 'es'} onClick={() => handleListItemClick('es')}>
                  <span>Español</span>
                </Dropdown.Item>
                <Dropdown.Item active={i18n === 'en'} onClick={() => handleListItemClick('en')}>
                  <span>English</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            {/* Notificaciones reales (db/041): sys_notifications + Reverb.
                Aquí estaba el marcador que decía "módulo pendiente"; ya existe. */}
            <NotificationsBell />
            {/* ── Nombre + último logout ── */}
            <Nav.Item className="pc-h-item d-none d-lg-flex align-items-center px-2" style={{ lineHeight: 1.2 }}>
              <div className="text-end">
                <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>
                  {/* Título (db/127) antes del nombre, solo si tiene valor */}
                  {user?.title?.trim() ? `${user.title.trim()} ` : ''}{user?.name} {user?.firstLastName}
                </div>
                <div className="text-muted" style={{ fontSize: '0.70rem' }}>
                  {lastLogout
                    ? <>Últ. logout: {fmtFechaHora(lastLogout)}</>
                    : <span className="text-success">Primera sesión</span>
                  }
                </div>
              </div>
            </Nav.Item>

            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle className="pc-head-link me-0 arrow-none" variant="link" id="settings-dropdown">
                <i className="ph ph-diamonds-four" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="pc-h-dropdown">
                <Dropdown.Item onClick={() => setShowMyAccount(true)}>
                  <i className="ph ph-user" />
                  Mi cuenta
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowChangePwd(true)}>
                  <i className="ph ph-lock-key" />
                  Cambiar contraseña
                </Dropdown.Item>
                {/* Emular usuario: solo superadmin, y solo si no está ya
                    emulando —desde dentro de una emulación el API lo rechaza
                    (el dueño del token ya no es superadmin), así que ofrecerlo
                    sería ofrecer un error.

                    Que el item no se pinte es comodidad, no la protección: las
                    tres rutas vuelven a comprobar `is_superadmin` en el
                    servidor. */}
                {user?.isSuperadmin && !impersonatedBy && (
                  <Dropdown.Item onClick={() => setShowImpersonate(true)}>
                    <i className="ph ph-user-switch" />
                    Ver como otro usuario
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={handleLogout}>
                  <i className="ph ph-power" />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </div>
      </div>
    </header>
    <ChangePasswordModal show={showChangePwd} onHide={() => setShowChangePwd(false)} />
    <MyAccountModal show={showMyAccount} onHide={() => setShowMyAccount(false)} />
    <ImpersonateModal show={showImpersonate} onHide={() => setShowImpersonate(false)} />
    </>
  );
}
